import React, { useEffect, useState } from 'react';
import styles from './NexsetStats.module.scss';
import { Stack } from 'new/elements/Stack';
import { Typography } from '@mui/material';
import { useResourceUsageMetrics } from 'new/hooks/adapters/useResourceUsageMetrics';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDataSetUpdateTime, fetchResourceMetricsAggregate } from 'js/actions';
import DateUtils from 'js/lib/DateUtils';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_DATA_SET_UPDATE_TIME_REQUEST,
  GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
} from 'js/constants/actionTypes';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import {
  getPresetDateRange,
  MetricsDateRange,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import { MetricsUtil } from 'new/utils/MetricsUtil';

interface NexsetStatsProps {
  resourceId: string;
}

export const NexsetStats = (props: NexsetStatsProps) => {
  const { resourceId } = props;
  const dispatch = useDispatch();
  const sampleUpdateTime = useSelector(
    (state: any) => state.dataSets?.sampleUpdateTimeById[resourceId],
  );
  const { data, fetch, usage } = useResourceUsageMetrics(resourceId, RESOURCE_TYPE_DATA_SET);

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(data);

  const loading = useIsLoading(
    GET_DATA_SET_UPDATE_TIME_REQUEST,
    GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
  );

  useEffect(() => {
    dispatch(fetchDataSetUpdateTime(resourceId));
    dispatch(fetchResourceMetricsAggregate(resourceId, RESOURCE_TYPE_DATA_SET));
    fetch(DateUtils.getPastDateRange(30));
  }, []);

  const [metricsRange, setMetricsRange] = useState(() => getPresetDateRange('30d'));

  const onDatesChanged = (dates: MetricsDateRange) => {
    setMetricsRange({ start: new Date(dates.start), end: new Date(dates.end) });
    fetch(dates);
  };

  const onRefresh = () => {
    dispatch(fetchDataSetUpdateTime(resourceId));
    dispatch(fetchResourceMetricsAggregate(resourceId, RESOURCE_TYPE_DATA_SET));
  };

  return (
    <Stack>
      <Stack className={styles.usageContainer}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={styles.usageTop}
        >
          <Typography variant="subtitle1">Stats</Typography>
          <Stack direction="row" gap="8px" alignItems="center">
            <Typography variant="body3">
              {DateUtils.convertDateStringToFormat(sampleUpdateTime)}
            </Typography>
          </Stack>
        </Stack>

        <MetricsPanel
          lifetimeMetrics={MetricsUtil.handleLifetimeMetrics(usage)}
          trendData={data}
          start={metricsRange.start}
          end={metricsRange.end}
          onDatesChanged={onDatesChanged}
          onRefresh={onRefresh}
          hideRecords={hideRecords}
          loading={loading}
          title="Data record"
          resourceType={RESOURCE_TYPE_DATA_SET}
          resourceId={resourceId}
        />
      </Stack>
    </Stack>
  );
};
